'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useCookieState, useLocalStorageState } from 'ahooks';
import { ArrowDownRight, X } from 'lucide-react';

import { Button } from '@components/ui/button';
import { CopyButton } from '@components/ui/copy-button';
import { Input } from '@components/ui/input';
import { cn } from '@components/utils/classname';
import { COOKIE_TOKEN } from '@shared-web/constants/cookies';
import { cookieDomain } from '@shared-web/modules/auth/utils/clearSession';

import { LanguageSwitcher } from '../Header/LanguageSwitcher';

interface DevtoolState {
  width: string;
  height: string;
  layout: 'left' | 'bottom';
}

interface DevtoolProps {
  onClose: () => void;
}

const DEVTOOL_STORAGE_KEY = 'x-webview.devtool';

/**
 * Simplified Devtool for Webview
 *
 * Shows basic debug information for the webview environment.
 * This is a simplified version of the web Devtool without auth-related features.
 */
export function Devtool({ onClose }: DevtoolProps) {
  const [show, setShow] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const [state, setState] = useLocalStorageState<DevtoolState>(
    DEVTOOL_STORAGE_KEY,
    {
      defaultValue: {
        width: '300px',
        height: '300px',
        layout: 'left',
      },
    },
  );

  const contentStyle =
    state?.layout === 'left'
      ? { width: state?.width ?? '300px' }
      : { width: '100%', height: state?.height ?? '300px' };

  const switchLayout = useCallback(() => {
    setState((prev) => ({
      ...prev!,
      layout: prev?.layout === 'left' ? 'bottom' : 'left',
    }));
  }, [setState]);

  const handleDragging = useCallback(
    (e: MouseEvent) => {
      if (state?.layout === 'left') {
        const width = e.clientX;
        if (width >= 200 && width <= 800) {
          setState((prev) => ({ ...prev!, width: `${width}px` }));
        }
      } else {
        const height = window.innerHeight - e.clientY;
        if (height >= 100 && height <= window.innerHeight) {
          setState((prev) => ({ ...prev!, height: `${height}px` }));
        }
      }
    },
    [state?.layout, setState],
  );

  const startDragging = useCallback(() => {
    document.addEventListener('mousemove', handleDragging);
  }, [handleDragging]);

  useEffect(() => {
    setShow(true);
    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleDragging);
    };
    document.body.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.body.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mousemove', handleDragging);
    };
  }, [handleDragging]);

  const isBottom = state?.layout === 'bottom';

  return (
    <div
      ref={containerRef}
      className={cn(
        'text-body2 text-primary-foreground bg-popover/95 fixed z-[9999] rounded transition-all duration-300 ease-out',
        isBottom ? 'right-0 bottom-0 left-0' : 'top-[60px] bottom-0 left-0',
        show
          ? 'translate-x-0 translate-y-0 opacity-100'
          : isBottom
            ? 'translate-y-full opacity-50'
            : '-translate-x-full opacity-50',
      )}
      style={contentStyle}
    >
      {/* Dragger */}
      <div
        className={cn(
          'bg-muted absolute flex items-center select-none',
          isBottom
            ? 'top-[-16px] right-0 left-0 h-4 cursor-row-resize justify-center'
            : 'top-0 right-0 bottom-0 w-4 cursor-col-resize',
        )}
        onMouseDown={startDragging}
      >
        <span
          className={cn(
            'border-border',
            isBottom
              ? 'h-2 w-5 border-t border-b'
              : 'ml-1 h-5 w-2 border-r border-l',
          )}
        />
      </div>

      {/* Content */}
      <div className="scrollbar-thin h-full overflow-y-auto p-4 pr-8">
        <div className="flex justify-end py-2">
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="size-4" />
          </Button>
        </div>

        <SessionToken />
        <LocaleSetting />
        <WebviewInfo />
      </div>

      {/* Layout Switch Button */}
      <Button
        variant="ghost"
        size="icon"
        className={cn('absolute right-6 bottom-2', isBottom && 'rotate-45')}
        onClick={switchLayout}
      >
        <ArrowDownRight className="size-4" />
      </Button>
    </div>
  );
}

function SessionToken() {
  const [newToken, setNewToken] = useState('');
  const [currentToken, setToken] = useCookieState(COOKIE_TOKEN, {
    defaultValue: '',
    domain: cookieDomain || undefined,
  });

  const handleSaveToken = () => {
    if (newToken) {
      setToken(newToken);
      window.location.reload();
    }
  };

  return (
    <div className="mt-4">
      <h3 className="text-label1 mb-2">Session Token</h3>
      {currentToken && (
        <>
          <div className="text-muted-foreground mt-2">current token</div>
          <CopyableText text={currentToken} />
        </>
      )}
      <Input
        value={newToken}
        onChange={(e) => setNewToken(e.target.value)}
        placeholder="Paste token and press enter"
        onKeyDown={(e) => e.key === 'Enter' && handleSaveToken()}
        className="bg-input text-primary-foreground placeholder:text-muted-foreground mt-2"
        size="sm"
      />
    </div>
  );
}

function LocaleSetting() {
  return (
    <div className="mt-4">
      <h3 className="text-label1 mb-2">Locale</h3>
      <div className="mt-2">
        <LanguageSwitcher />
      </div>
    </div>
  );
}

function WebviewInfo() {
  const info = {
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    cookieEnabled: navigator.cookieEnabled,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    devicePixelRatio: window.devicePixelRatio,
  };

  return (
    <div className="mt-4">
      <h3 className="text-label1 mb-2">Webview Info</h3>
      {Object.entries(info).map(([key, value]) => (
        <div key={key} className="mt-2">
          <div className="text-muted-foreground">{key}</div>
          <CopyableText text={String(value)} />
        </div>
      ))}
    </div>
  );
}

function CopyableText({ text }: { text: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-primary-foreground flex-1 truncate">
        {text || '-'}
      </span>
      <CopyButton content={text} variant="ghost" size="sm" />
    </div>
  );
}
